import { useState } from 'react'
import { toast } from 'sonner'
import { Database, Plus, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { useDeleteDatabaseConnector } from '@/hooks'
import { getErrorMessage } from '@/api/errors'
import { extractValidationMessage } from '@/lib/validationErrors'
import type { Connector } from '@/types'
import { DatabaseConnectionDialog } from './DatabaseConnectionDialog'
import { parseDatabaseMetadata } from './databaseConnectorMetadata'

interface DatabaseConnectorsSectionProps {
  projectId: string
  connectors: Connector[]
  canManage: boolean
}

function connectorLabel(connector: Connector): { primary: string; secondary: string } {
  const primary = connector.connectionName || 'Database connection'
  const metadata = parseDatabaseMetadata(connector.providerMetadata)
  if (!metadata) return { primary, secondary: '' }
  const location = metadata.host
    ? `${metadata.host}${metadata.port ? `:${metadata.port}` : ''}/${metadata.database}`
    : metadata.database
  const user = metadata.user ? ` · User: ${metadata.user}` : ''
  return { primary, secondary: `${metadata.dbType} — ${location}${user}` }
}

export function DatabaseConnectorsSection({ projectId, connectors, canManage }: Readonly<DatabaseConnectorsSectionProps>) {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Connector | null>(null)
  const [deleting, setDeleting] = useState<Connector | null>(null)
  const deleteConnector = useDeleteDatabaseConnector(projectId)

  const databaseConnectors = connectors.filter((c) => c.category === 'Database')

  const openAdd = () => {
    setEditing(null)
    setDialogOpen(true)
  }

  const openEdit = (connector: Connector) => {
    setEditing(connector)
    setDialogOpen(true)
  }

  const handleDelete = async () => {
    if (!deleting) return
    try {
      await deleteConnector.mutateAsync(deleting.id)
      toast.success('Database connection deleted')
      setDeleting(null)
    } catch (err) {
      toast.error(extractValidationMessage(err) ?? getErrorMessage(err) ?? 'Failed to delete database connection')
    }
  }

  return (
    <div className="space-y-3" data-testid="database-connectors-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database className="h-5 w-5 text-muted-foreground" />
          <h3 className="text-lg font-semibold">Databases</h3>
        </div>
        {canManage && (
          <Button size="sm" onClick={openAdd} data-testid="add-database-connection">
            <Plus className="h-4 w-4 mr-1" /> Add database connection
          </Button>
        )}
      </div>
      <p className="text-sm text-muted-foreground">
        Saved connections to the source and target databases for your migration workflows.
      </p>

      {databaseConnectors.length === 0 ? (
        <div className="rounded-lg border border-dashed p-6 text-sm text-muted-foreground text-center">
          No database connections yet.
        </div>
      ) : (
        <ul className="space-y-2">
          {databaseConnectors.map((connector) => {
            const { primary, secondary } = connectorLabel(connector)
            return (
              <li
                key={connector.id}
                className="flex items-center justify-between rounded-lg border p-3"
                data-testid={`database-connector-${connector.id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{primary}</p>
                  {secondary && <p className="text-xs text-muted-foreground truncate">{secondary}</p>}
                </div>
                {canManage && (
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(connector)} aria-label={`Edit ${primary}`} title="Edit" data-testid={`database-connector-${connector.id}-edit`}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => setDeleting(connector)} aria-label={`Delete ${primary}`} title="Delete" data-testid={`database-connector-${connector.id}-delete`}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}

      <DatabaseConnectionDialog
        projectId={projectId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        existing={editing}
      />

      <Dialog open={deleting !== null} onOpenChange={(open) => { if (!open) setDeleting(null) }}>
        <DialogContent className="sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle>Delete database connection?</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {deleting ? connectorLabel(deleting).primary : ''} will be removed and its stored credentials purged.
            Workflow runs that reference it will no longer resolve it.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteConnector.isPending} data-testid="confirm-delete-database-connector">
              {deleteConnector.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
